import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { resolveGuidTargets } from '@viewer/lib/resolveGuidTargets'
import type { Viewpoint } from '@viewer/lib/viewpoint'
import { pontoAFrenteDaCamara } from '@viewer/bcf/lib/pinAncora'
import { elementosDoTopico } from './bcfElementos'
import { caixaDeCorteA, enquadrar, seleccionar, type Alvo } from './bcfElementosCena'
import { aplicarRealce, limparRealce, realceActivo, type ItemPintado } from './bcfRealce'
import { contextoDaCena, lerVisualizacaoDaCena, type ContextoDeLeitura } from './bcfReferencial'
import {
  PREFIXO_PLANO_DO_FICHEIRO,
  caixaDosPlanos,
  intervaloDaCaixa,
  planosSoltos,
  type LeituraEscolhida,
} from './bcfVistaImportada'

/**
 * "Ver" de um viewpoint importado de um ficheiro BCF (`components_only`).
 *
 * Dois caminhos, decididos com os modelos abertos agora:
 *
 * - **Vista do ficheiro**: o `.bcfv` original relido (`bcfv_original`,
 *   com o referencial da regra comum e, se preciso, os outros, ver
 *   `bcfReferencial.ts`) dá uma câmara junto dos modelos e a olhar para
 *   eles. Aplica-se a câmara, os planos de corte (como caixa de corte
 *   quando formam uma, senão como planos soltos), a selecção e o realce
 *   (cada elemento da sua cor, o resto em fantasma). Ao contrário do
 *   viewpoint nosso, não se apagam as medições
 *   nem as anotações: o ficheiro não as traz, e o viewpoint não é da
 *   plataforma, por isso não há estado capturado a repor.
 * - **Elementos**: a câmara não serve (noutro referencial, longe dos
 *   modelos ou ausente). Faz-se o "Ver no 3D" dos Elementos ligados:
 *   selecção, cada elemento com a sua cor (A ciano e B magenta nos
 *   conflitos), o resto em fantasma e a câmara enquadrada só nos
 *   elementos. Os planos de corte do ficheiro não se aplicam, e a caixa
 *   de corte em uso desliga-se (podia esconder os elementos). A caixa à
 *   volta dos elementos fica como acção no aviso, e não automática: com
 *   uma laje entre os elementos, a caixa é o piso inteiro, e quem só quer
 *   ver não perde o corte que tinha sem o pedir.
 *
 * Os viewpoints importados antes desta regra ficaram gravados com a
 * câmara lida no referencial do modelo activo. Não se migram: a câmara
 * gravada não se usa aqui, relê-se sempre o `.bcfv` original. Sem o
 * original (importados antigos, de antes de se guardar), só há os
 * elementos.
 */

export type ModoDoVer = 'vista-do-ficheiro' | 'elementos' | 'nada'

export interface ResultadoDoVer {
  modo: ModoDoVer
  /** Elementos resolvidos nos modelos abertos. */
  elementos: number
  /** O que se fez com os planos de corte do ficheiro. */
  planos: 'caixa' | 'soltos' | 'nenhum' | 'ignorados'
  /** De onde veio o referencial da câmara (diagnóstico). */
  origem: string | null
  /** A mensagem mostrada no aviso. */
  mensagem: string
}

export const MENSAGEM_VISTA_DO_FICHEIRO = 'Vista do ficheiro BCF: câmara e cortes do viewpoint importado.'
export const MENSAGEM_ELEMENTOS =
  'A câmara do ficheiro não corresponde aos modelos abertos; mostram-se os elementos.'
export const MENSAGEM_SO_ELEMENTOS = 'O viewpoint importado não traz câmara utilizável; mostram-se os elementos.'
export const MENSAGEM_SEM_ELEMENTOS = 'Nenhum elemento do viewpoint existe nos modelos abertos.'

/** Quanto tempo fica o aviso com a acção da caixa de corte. */
export const DURACAO_DO_AVISO_MS = 8000

/** O realce (cores e fantasma) foi posto por um "Ver"? O "Ver" seguinte,
 *  de qualquer viewpoint, tira-o; o das secções "Elementos ligados" e do
 *  "Ver no 3D" dos blocos tem o seu próprio "Limpar". */
let realceDoVer = false

/** Tira as cores e o fantasma postos pelo último "Ver" de um importado.
 *  Chamado no início de cada "Ver" (`applyViewpoint`). */
export async function largarRealceDoVer(): Promise<void> {
  if (!realceDoVer) return
  realceDoVer = false
  if (realceActivo()) await limparRealce()
}

/** Decide o caminho, sem tocar na cena. Puro sobre a leitura. */
export function modoDoVer(leitura: LeituraEscolhida | null, elementos: number): ModoDoVer {
  if (leitura?.plausivel) return 'vista-do-ficheiro'
  return elementos > 0 ? 'elementos' : 'nada'
}

async function resolverPorElemento(vp: Viewpoint): Promise<ItemPintado[]> {
  const elementos = elementosDoTopico([{ id: 'ver', viewpoint: vp }])
  const modelos = useAppStore.getState().models
  const out: ItemPintado[] = []
  for (const e of elementos) {
    const alvos: Alvo[] = []
    for (const m of modelos) {
      const ids = await resolveGuidTargets(m.id, [e.guid]).catch(() => [] as number[])
      for (const localId of ids) alvos.push({ modelId: m.id, localId })
    }
    out.push({ cor: e.cor, alvos })
  }
  return out
}

async function tornarVisiveis(alvos: readonly Alvo[]): Promise<void> {
  const porModelo: Record<string, number[]> = {}
  for (const a of alvos) (porModelo[a.modelId] ??= []).push(a.localId)
  for (const [modelId, ids] of Object.entries(porModelo)) {
    await viewerApi.setItemsVisibility(modelId, ids, true)
  }
}

function tirarPlanosDoFicheiro(): void {
  const s = useAppStore.getState()
  for (const p of s.planosDeCorte ?? []) {
    if (p.id.startsWith(PREFIXO_PLANO_DO_FICHEIRO)) s.tirarPlano(p.id)
  }
}

function aplicarPlanos(leitura: LeituraEscolhida, ctx: ContextoDeLeitura): ResultadoDoVer['planos'] {
  const s = useAppStore.getState()
  tirarPlanosDoFicheiro()
  const planos = leitura.lida.clipping_planes
  if (planos.length === 0) {
    s.setSectionEnabled(false)
    return 'nenhum'
  }
  if (!leitura.planosPlausiveis) {
    s.setSectionEnabled(false)
    return 'ignorados'
  }
  const caixa = caixaDosPlanos(planos)
  if (caixa) {
    s.setSectionOrientation(caixa.orientation)
    s.setSectionExtents(caixa.extents)
    s.setSectionExtentRange(intervaloDaCaixa(caixa, ctx.caixa))
    s.setSectionEnabled(true)
    return 'caixa'
  }
  s.setSectionEnabled(false)
  for (const p of planosSoltos(planos)) s.acrescentarPlano(p)
  return 'soltos'
}

async function aplicarCamara(leitura: LeituraEscolhida, ctx: ContextoDeLeitura): Promise<void> {
  const world = viewerApi.getWorld()
  const cam = leitura.lida.camera
  if (!world || !cam) return
  const pos = cam.position
  // O BCF não tem alvo: o centro da órbita fica onde o olhar entra no
  // modelo (o mesmo ponto do pin), e não a 50 m fixos.
  const alvo = pontoAFrenteDaCamara(cam, ctx.caixa) ?? [
    pos.x + cam.direction.x * 50,
    pos.y + cam.direction.y * 50,
    pos.z + cam.direction.z * 50,
  ]
  try {
    await world.camera.controls.setLookAt(pos.x, pos.y, pos.z, alvo[0], alvo[1], alvo[2], true)
  } catch (err) {
    console.warn('[bcf] setLookAt do viewpoint importado falhou', err)
  }
}

/** Aplica um viewpoint importado (ver o comentário do ficheiro). */
export async function verViewpointImportado(vp: Viewpoint): Promise<ResultadoDoVer> {
  const store = useAppStore.getState()
  let ctx: ContextoDeLeitura
  try {
    ctx = contextoDaCena()
  } catch {
    ctx = { modelos: [], activoId: null, caixa: null }
  }
  const xml = vp.bcfv_original?.xml
  const leitura = xml ? lerVisualizacaoDaCena(xml, vp.model_refs ?? [], ctx) : null
  const itens = await resolverPorElemento(vp)
  const alvos = itens.flatMap((i) => i.alvos)
  const modo = modoDoVer(leitura, alvos.length)

  if (modo === 'vista-do-ficheiro' && leitura) {
    await aplicarCamara(leitura, ctx)
    const planos = aplicarPlanos(leitura, ctx)
    if (alvos.length > 0) {
      await tornarVisiveis(alvos)
      await seleccionar(alvos)
      // Também aqui cada elemento da sua cor e o resto em fantasma, como
      // no caminho dos elementos: dentro da caixa do ficheiro, os
      // elementos do conflito ficavam misturados com o resto (Rui,
      // 16/10/2026).
      await aplicarRealce(itens)
      realceDoVer = true
    } else {
      store.clearSelection()
    }
    store.pushToast(MENSAGEM_VISTA_DO_FICHEIRO, { type: 'info' })
    return {
      modo,
      elementos: alvos.length,
      planos,
      origem: leitura.origem,
      mensagem: MENSAGEM_VISTA_DO_FICHEIRO,
    }
  }

  if (modo === 'nada') {
    store.pushToast(MENSAGEM_SEM_ELEMENTOS, { type: 'info' })
    return { modo, elementos: 0, planos: 'ignorados', origem: leitura?.origem ?? null, mensagem: MENSAGEM_SEM_ELEMENTOS }
  }

  // Elementos.
  tirarPlanosDoFicheiro()
  store.setSectionEnabled(false)
  await tornarVisiveis(alvos)
  await seleccionar(alvos)
  // Aqui a caixa de corte pode entrar, se a preferência Caixa estiver
  // ligada (viewer/realce); na vista do ficheiro, os cortes são os dele.
  await aplicarRealce(itens, { caixa: true })
  realceDoVer = true
  await enquadrar(alvos)
  const mensagem = leitura?.lida.camera ? MENSAGEM_ELEMENTOS : MENSAGEM_SO_ELEMENTOS
  store.pushToast(mensagem, {
    type: 'info',
    timeout: DURACAO_DO_AVISO_MS,
    ...(useAppStore.getState().realce.caixa
      ? {}
      : { acao: { rotulo: 'Caixa de corte à volta', executar: () => void caixaDeCorteA(alvos) } }),
  })
  return {
    modo,
    elementos: alvos.length,
    planos: (leitura?.lida.clipping_planes.length ?? 0) > 0 ? 'ignorados' : 'nenhum',
    origem: leitura?.origem ?? null,
    mensagem,
  }
}
